import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { actionToneOf, BUTTON_SWITCH, CHATTER_MAX_CHARS, CHATTER_MAX_MENTIONS, MASTER_SWITCH, type Button } from '@claims/core';
import { api, type ActResult } from './api.js';
import { toneProps } from './action-tone.js';
import { Ic } from './icons.js';
import { usePress } from './press.js';
import { PressButton } from './press-button.js';

// v2 buttons: each has its own switch (all off by default). Today every press is a DRY RUN: the console says what the controller would
// do and records the press in the audit log; nothing is written to Salesforce. So each wears grey (the action tones, 2026-10-25; core
// action_tone.ts): it only says what would happen. Switched off, a button keeps its quiet off look whatever its tone.
export interface Field { name: string; label: string; type?: 'text' | 'check' | 'select'; options?: [string, string][]; showIf?: (v: Record<string, unknown>) => boolean }

/** Chatter the team: how many of the matter team show before "Show all" (the list grows in place, never its own scroller). */
const WHO_FEW = 8;
const REFUSED: Record<string, string> = {
  switch_off: 'This button is switched off.', claim_is_policy_or_placeholder: "That looks like the policy number or a placeholder, not a claim number.",
  record_has_claim: 'The record already has a different claim number.', already_on_record: 'That claim number is already on the record.',
  CAP_WEEKLY: 'The AI already called 3 times this week on this record.', no_dialable_phone: 'There is no phone number the AI can dial.',
  state_farm_website_only: 'State Farm takes claims on its website, not by phone.', text_once_a_day: 'This client was already texted today.',
  already_on_file: 'That carrier is already on an at-fault record.', second_def_needs_review: 'Another at-fault carrier is already on file; check if there are two at-fault parties.',
  no_police_report: 'No police report is in our records yet.', police_names_carrier: 'The police report names a carrier, so it is not "no insurance".',
  sr19_not_none: 'No DMV SR19 note says "no insurance" yet.', same_carrier: 'That is the carrier already on the record.', policy_unusable: "That doesn't look like a policy number.",
  phone_unusable: "That isn't a dialable phone number.", both_boxes_required: 'Tick both boxes.', row_not_on_matter: 'That record is not on this matter.',
  message_required: 'Write a message first.', pick_someone: 'Pick at least one person to tag.', too_many_mentions: `Tag ${CHATTER_MAX_MENTIONS} people at most.`,
  bad_mention: 'One of the people picked is not a Salesforce user.', mention_not_on_team: 'Someone picked is no longer on this matter team.',
  actions_not_configured: 'Buttons are not set up on this console yet.', context_unavailable: 'Salesforce or the claims system did not answer; try again.',
};
const said = (r: ActResult) => r.result === 'dry_run' ? `Dry run: would ${r.describes ?? r.path}. Recorded in the audit log; nothing was written.`
  : `Not done: ${REFUSED[r.code ?? ''] ?? (r.code ?? 'unknown').replace(/_/g, ' ')}`;

export function useSwitches() {
  return useQuery({ queryKey: ['switches'], queryFn: api.switches, staleTime: 30_000, retry: false });
}

export function Act({ label, help, pri, icon, button, fields, fixed, matterId, insuranceId, compact }: {
  label: string; help: string; pri?: boolean; icon?: string; button?: Button; fields?: Field[]; fixed?: Record<string, unknown>; matterId: string; insuranceId: string | null;
  /** on the row itself: a small button, the help in its hover text, the form and the answer as a popover */
  compact?: boolean;
}) {
  const sw = useSwitches();
  const on = !!button && !!sw.data && sw.data.switches[MASTER_SWITCH] === true && sw.data.switches[BUTTON_SWITCH[button]] === true;
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState<Record<string, unknown>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // ONE PRESS (press.ts): the button says "Saving (dry run)" with its spinner from the click, a second press while one is out sends nothing,
  // and a press the console never heard back from says so (it used to stay greyed for good)
  const press = usePress(async () => {
    try {
      const r = await api.act({ button: button!, matterId, insuranceId, input: { ...(fixed ?? {}), ...vals } });
      setMsg({ ok: r.result === 'dry_run', text: said(r) }); if (r.result === 'dry_run') setOpen(false);
    } catch { setMsg({ ok: false, text: 'Not done: the console did not answer; try again.' }); }
  });
  const busy = press.busy;
  const title = !button ? 'The system does this on its own' : on ? 'Dry run: says what would happen and records the press' : 'Switched off (v2 buttons start off)';
  const tone = button ? actionToneOf('v2', button) : null;
  const form: ReactNode = open && fields ? (
    <div className="iform">
      {fields.filter((f) => !f.showIf || f.showIf(vals)).map((f) => f.type === 'check'
        ? <label key={f.name} className="opt"><input type="checkbox" checked={vals[f.name] === true} onChange={(e) => setVals({ ...vals, [f.name]: e.target.checked })} /> {f.label}</label>
        : f.type === 'select'
          ? <label key={f.name} className="fld">{f.label}<select value={String(vals[f.name] ?? '')} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })}>
              <option value="">Pick one</option>{(f.options ?? []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
          : <label key={f.name} className="fld">{f.label}<input value={String(vals[f.name] ?? '')} onChange={(e) => setVals({ ...vals, [f.name]: e.target.value })} /></label>)}
      <div className="btnrow"><PressButton label="Save (dry run)" className="btn pri" busy={busy} onPress={press.run} tone={tone} />
        <button type="button" className="btn" disabled={busy} onClick={() => setOpen(false)}>Cancel</button></div>
    </div>
  ) : null;
  return (
    <span className={`fx${compact ? ' compact' : ''}`}>
      {/* a button with a form opens it (the form's Save is the press); one without presses at once */}
      {fields ? (
        <button type="button" className={`btn${pri ? ' pri' : ''}${compact ? ' sm' : ''}`} disabled={!on || busy} aria-disabled={!on} title={compact ? `${help}. ${title}` : title}
          {...toneProps(tone)} onClick={() => setOpen(!open)}>
          {icon ? <Ic name={icon} cls="sm" /> : null}{label}
        </button>
      ) : (
        <PressButton label={label} className={`btn${pri ? ' pri' : ''}${compact ? ' sm' : ''}`} disabled={!on} busy={busy} onPress={press.run} title={compact ? `${help}. ${title}` : title}
          tone={tone}>
          {icon ? <Ic name={icon} cls="sm" /> : null}{label}
        </PressButton>
      )}
      {compact ? null : <span className="fxh">{help}</span>}
      {form}
      {msg ? <span className={`fxh${compact ? ' pop' : ''} ${msg.ok ? 'msg-ok' : 'msg-no'}`}>{msg.text}</span> : null}
    </span>
  );
}

export const CANT_FIELDS: Field[] = [
  { name: 'why', label: 'Why', type: 'select', options: [['wrong', 'Wrong carrier'], ['nocov', 'No coverage on this policy'], ['already', 'Claim already open'], ['more', 'Carrier needs more info'], ['other', 'Something else']] },
  { name: 'carrier', label: 'The right carrier', showIf: (v) => v.why === 'wrong' },
  { name: 'claim', label: 'The claim number', showIf: (v) => v.why === 'already' },
  { name: 'what', label: 'What does the carrier need?', showIf: (v) => v.why === 'more' },
  { name: 'note', label: 'What happened?', showIf: (v) => v.why === 'other' },
];

/** "Chatter the team": one post on the matter's Salesforce Chatter feed that @mentions the people picked from the matter team. The
 * Claims-role member and the case manager come pre-ticked; desk users ("Accounting Lead") are listed but never pre-ticked. */
export function ChatterTeam({ matterId, compact }: { matterId: string; compact?: boolean }) {
  const sw = useSwitches();
  const on = !!sw.data && sw.data.switches[MASTER_SWITCH] === true && sw.data.switches[BUTTON_SWITCH.chatter_team] === true;
  const [open, setOpen] = useState(false);
  const team = useQuery({ queryKey: ['team', matterId], queryFn: () => api.team(matterId), enabled: open, staleTime: 60_000, retry: false });
  const [picked, setPicked] = useState<Record<string, boolean> | null>(null);
  const [message, setMessage] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const members = team.data?.team ?? [];
  const isPicked = (id: string, dflt: boolean) => (picked ? picked[id] === true : dflt);
  const chosen = members.filter((t) => isPicked(t.userId, t.picked)).map((t) => t.userId);
  // ONE PRESS (press.ts): "Posting (dry run)" with its spinner from the click; a second press while one is out sends nothing
  const press = usePress(async () => {
    try {
      const r = await api.act({ button: 'chatter_team', matterId, insuranceId: null, input: { message: message.trim(), mentions: chosen } });
      setMsg({ ok: r.result === 'dry_run', text: said(r) });
      if (r.result === 'dry_run') { setOpen(false); setMessage(''); setPicked(null); }
    } catch {
      setMsg({ ok: false, text: 'Not done: the console did not answer; try again.' });
    }
  });
  const busy = press.busy;
  // the team list grows in place (Rod 10-08: no scroll bars inside a page): the first WHO_FEW, then "Show all"
  const [allWho, setAllWho] = useState(false);
  const hiddenPicked = members.slice(WHO_FEW).filter((t) => isPicked(t.userId, t.picked)).length;
  const toggle = (id: string, dflt: boolean) => {
    const base = picked ?? Object.fromEntries(members.map((t) => [t.userId, t.picked]));
    setPicked({ ...base, [id]: !isPicked(id, dflt) });
  };
  return (
    <span className={`fx chat${compact ? ' compact' : ''}`}>
      {/* the labelled button wears its press's grey (a dry run that would hand the matter to its team); the icon in a case's tools keeps the
          tools' quiet look */}
      <button type="button" className={compact ? 'iconbtn sm' : 'btn'} disabled={!on || busy} aria-disabled={!on} aria-label="Chatter the team"
        title={on ? "Chatter the team: post on this matter's Chatter in Salesforce and tag the team (dry run)" : 'Chatter the team (switched off: v2 buttons start off)'}
        {...toneProps(compact ? null : actionToneOf('v2', 'chatter_team'))} onClick={() => { setOpen(!open); setMsg(null); }}>
        <Ic name="msg" cls="sm" />{compact ? null : 'Chatter the team'}
      </button>
      {open ? (
        <div className="iform chatform">
          {team.isLoading ? <span className="fxh">Loading the matter team…</span> : null}
          {team.isError ? <span className="fxh msg-no">Salesforce did not answer; try again.</span> : null}
          {members.length ? <div className="chatwho">{(allWho || members.length <= WHO_FEW ? members : members.slice(0, WHO_FEW)).map((t) => (
            <label key={t.userId} className="opt"><input type="checkbox" checked={isPicked(t.userId, t.picked)} onChange={() => toggle(t.userId, t.picked)} />
              {' '}{t.name}{t.role ? <span className="mt"> · {t.role}</span> : null}</label>))}
            {members.length > WHO_FEW ? <button type="button" className="linkbtn whomore" aria-expanded={allWho} onClick={() => setAllWho(!allWho)}>
              {allWho ? 'Show fewer' : `Show all ${members.length}${hiddenPicked ? ` (${hiddenPicked} more tagged)` : ''}`}</button> : null}</div>
            : team.isSuccess ? <span className="fxh">Nobody is on this matter team in Salesforce.</span> : null}
          <label className="fld">Message<textarea rows={3} maxLength={CHATTER_MAX_CHARS} value={message} onChange={(e) => setMessage(e.target.value)} /></label>
          <div className="btnrow">
            <PressButton label={`Post (dry run)${chosen.length ? `, tag ${chosen.length}` : ''}`} className="btn pri" busy={busy} onPress={press.run}
              disabled={!message.trim() || chosen.length === 0 || chosen.length > CHATTER_MAX_MENTIONS} tone={actionToneOf('v2', 'chatter_team')} />
            <button type="button" className="btn" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
      {msg ? <span className={`fxh${compact ? ' pop' : ''} ${msg.ok ? 'msg-ok' : 'msg-no'}`}>{msg.text}</span> : null}
    </span>
  );
}
